// Resolves the per-org display labels for the five generic
// `field1`..`field5` columns on `sites`. Overrides live in
// `orgs.preferences.siteFieldLabels`; any missing / blank slot falls
// back to `Custom field N` so the UI always renders something
// meaningful. Cached against the shared ['org'] query key so every
// consumer piggybacks on the same fetch.

import { useQuery } from '@tanstack/react-query'
import type { Org, OrgPreferences } from '@cmms/types'
import { api } from '../lib/api'

export interface SiteFieldLabels {
  field1: string
  field2: string
  field3: string
  field4: string
  field5: string
}

const DEFAULTS: SiteFieldLabels = {
  field1: 'Custom field 1',
  field2: 'Custom field 2',
  field3: 'Custom field 3',
  field4: 'Custom field 4',
  field5: 'Custom field 5',
}

export function useSiteFieldLabels(): SiteFieldLabels {
  const orgQ = useQuery<Org>({
    queryKey: ['org'],
    queryFn: () => api.orgs.getCurrent(),
    staleTime: 5 * 60_000,
  })
  const saved = (orgQ.data?.preferences as OrgPreferences | undefined)?.siteFieldLabels ?? {}
  return {
    field1: pick(saved.field1, DEFAULTS.field1),
    field2: pick(saved.field2, DEFAULTS.field2),
    field3: pick(saved.field3, DEFAULTS.field3),
    field4: pick(saved.field4, DEFAULTS.field4),
    field5: pick(saved.field5, DEFAULTS.field5),
  }
}

function pick(value: string | undefined, fallback: string): string {
  if (value == null) return fallback
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : fallback
}
